import type { components } from '../../../../api/generated/schema';
import type { AlignerSetStatus, UpdateAlignerSetPayload } from '../../../../api/treatment-ops';

export const MINUTES_PER_DAY = 1440;
export const MINUTES_PER_HOUR = 60;

export type AlignerSet = components['schemas']['AdminAlignerSetItem'];

export type DisplayStatus = 'not_started' | 'wearing' | 'paused' | 'completed';

export type FormState = {
  readonly status: DisplayStatus;
  readonly days: string;
  readonly hours: string;
};

export const STATUS_OPTIONS: readonly DisplayStatus[] = [
  'not_started',
  'wearing',
  'paused',
  'completed',
];

export function resolveDisplayStatus(aligner: AlignerSet): DisplayStatus {
  if (aligner.status === 'wearing' && aligner.paused_at != null) return 'paused';
  return aligner.status;
}

export function isAlignerSetStatus(value: string): value is AlignerSetStatus {
  return (
    value === 'not_started' || value === 'wearing' || value === 'paused' || value === 'completed'
  );
}

export function isDisplayStatus(value: string): value is DisplayStatus {
  return (
    value === 'not_started' || value === 'wearing' || value === 'paused' || value === 'completed'
  );
}

export function deriveInitialForm(aligner: AlignerSet): FormState {
  const totalMinutes = Math.max(0, aligner.effective_worn_minutes);
  const days = Math.floor(totalMinutes / MINUTES_PER_DAY);
  const hours = Math.floor((totalMinutes - days * MINUTES_PER_DAY) / MINUTES_PER_HOUR);
  return {
    status: resolveDisplayStatus(aligner),
    days: String(days),
    hours: String(hours),
  };
}

type BuildPatchArgs = {
  readonly initial: FormState;
  readonly form: FormState;
  readonly aligner: AlignerSet;
  readonly computedMinutes: number;
  readonly statusChanged: boolean;
  readonly wearChanged: boolean;
};

/**
 * Construct a minimal PATCH body honouring `model_fields_set` semantics:
 * - omit unchanged fields,
 * - explicit `null` clears a stamp (e.g. resume from paused),
 * - new ISO timestamps are stamped client-side only when transitioning into
 *   paused so the UI is responsive; resume/complete delegate to backend.
 *
 * `total_worn_minutes` is set directly to `computedMinutes` (the admin's
 * desired effective wear time). The backend closes any open WearingSession
 * and reopens a fresh one without accumulation, so writing
 * `total_worn_minutes = computedMinutes` makes the dashboard report exactly
 * `computedMinutes` immediately after the PATCH.
 */
export function buildPatch({
  form,
  aligner,
  computedMinutes,
  statusChanged,
  wearChanged,
}: BuildPatchArgs): Partial<UpdateAlignerSetPayload> {
  // `UpdateAlignerSetPayload` declares each field as `readonly`. We build the
  // patch mutably here for clarity, then return it under the readonly shape.
  type MutablePatch = { -readonly [K in keyof UpdateAlignerSetPayload]: UpdateAlignerSetPayload[K] };
  const patch: Partial<MutablePatch> = {};

  if (wearChanged) {
    patch.total_worn_minutes = Math.max(0, computedMinutes);
  }

  if (statusChanged) {
    const displayStatus = form.status;
    const wasPaused = aligner.status === 'wearing' && aligner.paused_at != null;

    // Only PATCH the underlying status when the admin selects an actual
    // status transition (not just toggling pause/resume on a wearing set).
    if (displayStatus === 'paused') {
      if (!wasPaused) {
        // Resume from non-paused → paused (mark pause stamp; leave status as-is
        // if currently 'wearing', otherwise transition into 'wearing' + paused).
        if (aligner.status !== 'wearing') {
          patch.status = 'wearing';
        }
        patch.paused_at = new Date().toISOString();
      }
    } else if (isAlignerSetStatus(displayStatus)) {
      // Display status is 'not_started' | 'wearing' | 'completed'.
      if (displayStatus !== aligner.status) {
        patch.status = displayStatus;
      }
      if (wasPaused && (displayStatus === 'wearing' || displayStatus === 'not_started')) {
        patch.paused_at = null;
      }
      if (displayStatus === 'not_started') {
        patch.started_at = null;
        patch.completed_at = null;
      }
    }
  }

  return patch;
}
